import React, { useState } from "react";
import { Play } from "lucide-react";
import FAQItem from "./FAQItem";

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqData = [
    {
      question: "How quickly can we start using SmileReveal™?",
      answer:
        "Most dental practices are up and running with SmileReveal within minutes. There's no complex setup, hardware installation, or staff training required. Simply create your account, upload a patient photo, and generate your first AI smile simulation instantly. SmileReveal works in any modern web browser and is fully mobile-compatible.",
    },
    {
      question: "Do we need special equipment or software?",
      answer:
        "No. SmileReveal™ is cloud-based dental smile simulation software that works from any device with a web browser — desktop, tablet, or smartphone. All you need is a patient photo taken with any smartphone or camera. No special lighting, intraoral scanners, or additional hardware required. The AI does all the work.",
    },
    {
      question: "Is SmileReveal™ HIPAA compliant?",
      answer:
        "Yes. SmileReveal™ is fully HIPAA compliant. All patient photos and smile simulations are encrypted with AES-256 encryption and processed on HIPAA-compliant infrastructure. We maintain strict access controls, audit logging, and data handling procedures that meet and exceed healthcare data security requirements. Your patients\u2019 data is protected by the same standards used by major healthcare systems.",
    },
    {
      question: "How does SmileReveal™ improve case acceptance?",
      answer:
        "SmileReveal™ improves dental case acceptance rates to 60%+ by giving patients a photorealistic preview of their treatment outcome before they commit. When patients can see exactly what their dental implants, veneers, or All-on-X restoration will look like using their own face, uncertainty is replaced with excitement. Studies show that patients who see a visual simulation of their treatment are significantly more likely to accept and commit — often during the same consultation.",
    },
    {
      question:
        "Can SmileReveal™ integrate with our existing practice management system?",
      answer:
        "SmileReveal™ is designed to work alongside your existing dental practice management software and clinical workflow. The Growth and Scale plans include integration capabilities with popular PMS systems, CRM tools, and marketing platforms. For DSOs and multi-location practices, the Scale plan offers custom integrations tailored to your specific technology stack — including API access for advanced workflows.",
    },
    {
      question: "Is there a contract or long-term commitment?",
      answer:
        "No. SmileReveal™ has no long-term contracts or commitments. All plans are month-to-month — you can upgrade, downgrade, or cancel anytime. We also offer annual billing with a discount for practices that want to save. We believe in earning your business every month, not locking you in.",
    },
    {
      question: "How much does AI smile simulation software cost?",
      answer:
        "SmileReveal™ AI smile simulation software starts at $99/month for the Essential plan, which includes up to 40 simulations per month and photorealistic previews. The Growth plan is $299/month and includes unlimited simulations, animated video previews, custom shade previews, a website lead capture tool, and an analytics dashboard. For DSOs and multi-location dental practices, the Scale plan offers custom pricing with white-label branding, multi-location management, and dedicated support. All plans include a free trial with no credit card required.",
    },
    {
      question: "What\u2019s the ROI of SmileReveal™ for a dental practice?",
      answer:
        "The average dental implant case is worth $3,000\u2013$50,000+ in revenue. If SmileReveal helps you close even 2\u20133 additional cases per month, the software pays for itself many times over. Practices using SmileReveal report case acceptance rate increases from 30\u201340% (industry average) to 60%+. At $99\u2013299/month, the ROI is typically 10\u201350x within the first month of use.",
    },
    {
      question: "How does SmileReveal™ compare to PreVu and other smile simulation tools?",
      answer:
        "SmileReveal™ differentiates from tools like PreVu Dental and SmileFy with AI-powered instant generation (no manual editing), 98.7% prediction accuracy, animated video previews, a built-in website lead capture widget for All-on-X lead generation, and custom shade previews. While other tools require manual editing or specialized hardware, SmileReveal generates photorealistic simulations from a single photo in seconds.",
    },
    {
      question: "Can SmileReveal™ be used as a lead capture tool on my dental website?",
      answer:
        "Yes — this is one of SmileReveal\u2019s most powerful features. Available on the Growth and Scale plans, SmileReveal can be installed as an exit-intent lead capture widget directly on your dental practice website. When visitors are about to leave, they\u2019re prompted to upload a photo and preview their future smile. Each completed simulation becomes a qualified lead — pre-framed for dental implant, All-on-X, or cosmetic treatment before they even call your office.",
    },
  ];

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <section className="py-20 px-3 sm:px-4 lg:px-6 max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        {/* Left Column: Title & Video Thumbnail */}
        <div className="flex flex-col space-y-8 lg:sticky lg:top-24">
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#EAF4FF] text-blue-600 text-[13px] font-bold mb-6 tracking-wide">
              AI Smile Simulation Software FAQ
            </span>
            <h2 className="text-[34px] sm:text-[44px] font-bold text-slate-900 leading-[1.1] tracking-tight">
              Frequently Asked
              <br />
              <span className="text-slate-400 font-medium">
                Questions About SmileReveal
              </span>
            </h2>
          </div>

          <div className="relative aspect-[4/3] rounded-[32px] overflow-hidden shadow-2xl group cursor-pointer">
            <img
              src="/faq-thumb.webp"
              alt="Happy dental patient"
              width="800"
              height="600"
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            {/* Overlay Gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

            {/* Play Button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-xl transform transition-all duration-300 group-hover:scale-110">
                <Play className="w-8 h-8 text-blue-600 fill-blue-600 translate-x-1" />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Accordion */}
        <div className="flex flex-col space-y-8">
          {faqData.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => handleToggle(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
